/**
 * Pricing service for product price calculations
 */
export class PricingService {
  /**
   * Calculate total price for package components
   * @param {Array} prices - Array of price objects from API
   * @param {Array} components - Array of component objects with qty
   * @param {string} netsuiteId - Main product NetSuite ID
   * @returns {Object} Object with item_price and priceschedule
   */
  static calculatePackagePrice(prices, components, netsuiteId) {
    let item_price = 0;
    const priceschedule = [];
    const itemPrices = [];
    const priceRanges = {};

    // Find main product
    const mainProduct = prices.find(p => p.internalid === netsuiteId);
    
    // Calculate price for each component
    prices.forEach(priceItem => {
      if (priceItem.internalid === netsuiteId) {
        return; // Skip main product
      }

      // Find matching component
      const component = components.find(c => c.product.netSuiteId === priceItem.internalid);
      if (!component || !priceItem.onlinecustomerprice_detail) {
        return;
      }

      const componentPrice = priceItem.onlinecustomerprice_detail.onlinecustomerprice;
      item_price += componentPrice * component.qty;

      // Process price schedule
      if (priceItem.onlinecustomerprice_detail.priceschedule) {
        priceItem.onlinecustomerprice_detail.priceschedule.forEach(scheduleItem => {
          const item = {
            ...scheduleItem,
            qty: component.qty
          };
          itemPrices.push(item);
          priceRanges[scheduleItem.minimumquantity] = scheduleItem.minimumquantity;
          priceRanges[scheduleItem.maximumquantity || '+'] = scheduleItem.maximumquantity || '+';
        });
      } else {
        itemPrices.push({
          minimumquantity: 0,
          price: componentPrice,
          qty: component.qty
        });
      }
    });

    // Build price schedule ranges
    const keys = Object.keys(priceRanges).sort((a, b) => {
      if (a === '+') return 1;
      if (b === '+') return -1;
      return Number(a) - Number(b);
    });

    for (let i = 0; i < keys.length - 1; i++) {
      const priceRangeItem = {
        minimumquantity: keys[i],
        maximumquantity: keys[i + 1],
        price: 0,
        items: []
      };

      itemPrices.forEach(itemPrice => {
        const minQty = Number(itemPrice.minimumquantity);
        const maxQty = itemPrice.maximumquantity === '+' ? Infinity : Number(itemPrice.maximumquantity);
        const rangeMin = Number(keys[i]);
        const rangeMax = keys[i + 1] === '+' ? Infinity : Number(keys[i + 1]);

        if (minQty <= rangeMin && maxQty >= rangeMax) {
          priceRangeItem.price += itemPrice.price * itemPrice.qty;
          priceRangeItem.items.push(itemPrice);
        }
      });

      priceschedule.push(priceRangeItem);
    }

    return {
      item_price,
      priceschedule,
      item: mainProduct
    };
  }

  /**
   * Process single product pricing
   * @param {Object} priceItem - Price object from API
   * @returns {Object} Object with item_price, priceschedule, and item
   */
  static processSingleProductPrice(priceItem) {
    let item_price = 0;
    let priceschedule = [];

    if (priceItem.onlinecustomerprice_detail) {
      item_price = priceItem.onlinecustomerprice_detail.onlinecustomerprice;
      
      if (priceItem.onlinecustomerprice_detail.priceschedule) {
        priceschedule = [...priceItem.onlinecustomerprice_detail.priceschedule];
        // Set maximum quantity to '+' for last item if only 2 items
        if (priceschedule.length === 2) {
          priceschedule[1].maximumquantity = '+';
        }
      }
    }

    return {
      item_price,
      priceschedule,
      item: priceItem
    };
  }

  /**
   * Get price count class for CSS
   * @param {Array} priceschedule - Price schedule array
   * @returns {string} CSS class name
   */
  static getPriceCountClass(priceschedule) {
    return `items_${priceschedule.length}`;
  }
}

